<template>
  <RemotePaginatedList :hide-search="hideSearch"
                       :filter-options="filterOptions"
                       :excluded-filter-fields="excludeFilterFields"
                       :headers="headers"
                       multi-sort
                       :show-select="showSelect"
                       v-model="selectedItemIds"
                       data-url="hkiarace/admin/user/"
                       data-key="users"
                       :refresh-trigger.sync="innerRefreshTrigger"
                       v-bind="$attrs"
                       v-on="$listeners">
    <template #item.name_en="{item}">
      {{ `${item.surname_en}, ${item.given_name_en}` }}
    </template>
    <template #item.name_zh="{item}">
      {{ `${item.surname_zh}${item.given_name_zh}` }}
    </template>
    <template #item.sasv.id_type="{value}">
      {{ value ? $t(`id_type.${value}`) : '' }}
    </template>
    <template #item.id_expiry_date="{value}">
      {{ value ? $parseDate(value, 'yyyy-MM-dd') : '' }}
    </template>
    <template #item.sasv.status="{value}">
      {{ value ? $t(`sasv_status.${value}`) : 'N/A' }}
    </template>
    <template #item.account.last_login="{value}">
      {{ value ? $parseDate(value) : '' }}
    </template>
    <template #item.account.locked_until="{item}">
      <v-btn icon
             v-if="userLocked(item)"
             @click.stop="$hasPermission('hkiarace.edit_member') ? unlockUser(item) : null">
        <v-icon>fas fa-lock</v-icon>
      </v-btn>
    </template>
    <template #item.acc_suspend_until="{item}">
      <v-btn icon
             v-if="userAccSuspended(item)"
             @click.stop="$hasPermission('hkiarace.edit_member') ? removeUserAccSuspension(item) : null">
        <v-icon>fas fa-lock</v-icon>
      </v-btn>
    </template>
    <template v-slot:item.actions="{item}">
      <v-container>
        <v-row>
          <v-col>
            <v-tooltip top
                       key="edit">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="editItem(item)">
                  <v-icon>fas fa-edit</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="item.account.disabled && $hasPermission('hkiarace.edit_member')">
            <v-tooltip top
                       key="enable">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="enableUser(item)">
                  <v-icon>fas fa-user-check</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('enable') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="!item.account.disabled && $hasPermission('hkiarace.edit_member')">
            <v-tooltip top
                       key="disable">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="disableUser(item)">
                  <v-icon>fas fa-user-times</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('disable') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="item.id_no && !item.id_verified_at && $hasPermission('hkiarace.verify_member_id')">
            <v-tooltip top
                       key="verify">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="startVerifyUser(item)">
                  <v-icon>fas fa-id-card</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_user.validate_id_title') }}</span>
            </v-tooltip>
            <!--          <font-awesome-icon icon="id-card"-->
            <!--                             v-b-tooltip.hover title="Verify ID"-->
            <!--                             @click.stop="startVerifyUser(item)"></font-awesome-icon>-->
          </v-col>
          <v-col v-if="$hasPermission('hkiarace.edit_member')">
            <v-tooltip top
                       key="addLoyaltyPoint">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="addLoyaltyPoint(item)">
                  <v-icon>fas fa-funnel-dollar</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_user.add_point_title') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-if="$hasPermission('hkiarace.edit_member')">
            <v-tooltip top
                       key="addGymPass">
              <template #activator="{on, attrs}">
                <v-btn icon
                       x-small
                       v-on="on"
                       v-bind="attrs"
                       @click="addGymPass(item)">
                  <v-icon>fas fa-dumbbell</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('admin_user.add_gym_pass_title') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </template>
  </RemotePaginatedList>
</template>

<script>
import {computed, onMounted, ref, watch} from '@vue/composition-api'
import RemotePaginatedList from "@/components/RemotePaginatedList";
import {DateTime} from "luxon";
import TextInputDialog from "@/components/TextInputDialog";
import AdminAddGymPassDialog from "@/components/acc_booking/AdminAddGymPassDialog";

export default {
  name: 'MemberList',
  components: {RemotePaginatedList},
  props: {
    hideSearch: Boolean,
    showSelect: Boolean,
    excludeFilterFields: Array,
    refreshTrigger: Boolean,
  },
  setup(props, {root, emit}) {
    const refresh = function () {
      innerRefreshTrigger.value = true;
      getCompanies();
    };
    onMounted(() => {
      refresh();
    });

    watch(() => props.refreshTrigger, (newValue) => {
      if (newValue) {
        refresh();
        emit('update:refresh-trigger', false);
      }
    })

    const companies = ref([]);
    const getCompanies = function () {
      root.$callAPI('hkiarace/admin/company/', {
        page_size: -1
      }, function (response) {
        companies.value = response.body.companies;
      });
    };

    // Filter
    const filterOptions = computed(() => {
      return [
        {text: root.$t('admin_user.serial_no'), value: 'id', contains: true},
        {text: root.$t('admin_user.email'), value: 'account.email', contains: true},
        {
          text: root.$t('admin_user.company'), value: 'company.id',
          options: companies.value.map((c) => {
            return {text: c.name, value: c.id};
          }),
        },
        { text: root.$t('admin_user.sasv_name'), value: 'sasv.name', contains: true},
        {
          text: root.$t('admin_user.id_type'), value: 'sasv.id_type',
          options: [
            {text: root.$t('id_type.AIRPORT_OCTOPUS'), value: 'AIRPORT_OCTOPUS'},
            {text: root.$t('id_type.VIP'), value: 'VIP'},
            // {text: root.$t('id_type.PERSONAL_OCTOPUS_AND_STAFF_CARD'), value: 'PERSONAL_OCTOPUS_AND_STAFF_CARD'},
          ],
        },
        {text: root.$t('admin_user.id_no'), value: 'sasv.octopus_no', contains: true},
        {
          text: root.$t('my_account.id_verified_at'), value: 'sasv.status',
          options: [
            {text: root.$t('sasv_status.PENDING_APPROVAL'), value: 'PENDING_APPROVAL'},
            {text: root.$t('sasv_status.APPROVED'), value: 'APPROVED'},
            {text: root.$t('sasv_status.REJECTED'), value: 'REJECTED'},
            {text: root.$t('sasv_status.SUSPENDED'), value: 'SUSPENDED'},
          ],
        },
        {
          text: root.$t('admin_user.sasv_not_submitted'), value: 'sasv', single: true,
          options: [
            {text: root.$t('yes'), value: false},
            {text: root.$t('no'), value: true},
          ],
        },
        // {text: root.$t('not_verified'), value: 'id_verified_at', boolean: false},
        {text: root.$t('my_account.acc_membership_valid_until'), value: 'acc_membership_valid_until', date_range: true},
        {text: root.$t('admin_user.last_login'), value: 'account.last_login', date_range: true},
        {
          text: root.$t('disable'), value: 'account.disabled', options: [
            {text: root.$t('yes'), value: true},
            {text: root.$t('no'), value: false},
          ]
        },
        {
          text: root.$t('my_account.receive_promotional_email'), value: 'receive_promotional_email',
          options: [
            {text: root.$t('yes'), value: true},
            {text: root.$t('no'), value: false},
          ],
        },
      ]
    })

    // Data Table
    const headers = ref([
      {text: root.$t('admin_user.serial_no'), value: 'id'},
      {text: root.$t('admin_user.email'), value: 'account.email'},
      {text: root.$t('admin_user.name_en'), value: 'name_en', sortable: false},
      {text: root.$t('admin_user.name_zh'), value: 'name_zh', sortable: false},
      {text: root.$t('admin_user.company'), value: 'company.name'},
      // {text: root.$t('admin_user.id_type'), value: 'sasv.id_type'},
      {text: root.$t('admin_user.id_no'), value: 'sasv.octopus_no'},
      {text: root.$t('my_account.id_verified_at'), value: 'sasv.status'},
      // {text: root.$t('admin_user.id_expiry_date'), value: 'id_expiry_date'},
      {text: root.$t('admin_user.last_login'), value: 'account.last_login'},
      {text: root.$t('admin_user.locked_until'), value: 'account.locked_until'},
      {text: root.$t('admin_user.acc_suspend_until'), value: 'acc_suspend_until'},

      {text: '', value: 'actions', sortable: false},
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    watch(() => selectedItemIds.value, (newValue) => {
      emit('input', newValue);
    });

    const userLocked = function (item) {
      return item.account.locked_until && DateTime.fromISO(item.account.locked_until) > new Date();
    };
    const unlockUser = function (item) {
      if (confirm(root.$t('admin_user.confirm_unlock_msg'))) {
        root.$callAPI('hkiarace/admin/user/unlock/', {id: item.account.id}, function (response) {
          refresh();
        })
      }
    };

    const userAccSuspended = function (item) {
      return item.acc_suspend_until && DateTime.fromISO(item.acc_suspend_until) > new Date();
    };
    const removeUserAccSuspension = function (item) {
      if (confirm(root.$t('admin_user.confirm_remove_suspension_msg'))) {
        root.$callAPI('aafb/admin/remove_suspension/', {id: item.account.id}, () => {
          refresh();
        })
      }
    }

    const editItem = function (item) {
      emit('edit', item);
    };
    const enableUser = function (item) {
      if (confirm(root.$t('admin_user.confirm_enable_msg'))) {
        root.$callAPI('hkiarace/admin/user/enable/', {id: item.account.id}, () => {
          refresh();
        })
      }
    };
    const disableUser = function (item) {
      if (confirm(root.$t('admin_user.confirm_disable_msg'))) {
        root.$callAPI('hkiarace/admin/user/disable/', {id: item.account.id}, () => {
          refresh();
        })
      }
    };
    const startVerifyUser = function (item) {
      emit('verify', item);
    }
    const addLoyaltyPoint = async function (item) {
      const amount = await root.$dialog.showAndWait(TextInputDialog, {
        title: root.$t('admin_user.add_point_title'), label: root.$t('admin_acc_settings.top_up_tier_amount'), prefill: '0'
      });

      if (amount === undefined) return;

      root.$callAPI('hkiarace/admin/user/add_point/', {id: item.account.id, amount}, () => {
        root.$showAlertMessage(root.$t('admin_user.add_point_success_msg'));
      });
    }
    const addGymPass = async function (item) {
      const typeId = await root.$dialog.showAndWait(AdminAddGymPassDialog, {});

      if (typeId === undefined) return;

      root.$callAPI('hkiarace/admin/user/add_gym_pass/', {id: item.account.id, type_id: typeId}, () => {
        root.$showAlertMessage(root.$t('admin_user.add_gym_pass_success_msg'));
      });
    }

    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,

      userLocked,
      unlockUser,

      userAccSuspended,
      removeUserAccSuspension,

      editItem,

      enableUser,
      disableUser,

      startVerifyUser,
      addLoyaltyPoint,
      addGymPass,
    }
  }
}
</script>
